export interface EmbedConfig {
  partner: string;
  theme: 'light' | 'dark';
  locale: string;
  featureFlags: Record<string, boolean | number | string>;
  isEmbed: boolean;
}

const DEFAULT_CONFIG: EmbedConfig = {
  partner: 'default',
  theme: 'light',
  locale: 'en-US',
  featureFlags: {},
  isEmbed: false,
};

/**
 * True when the app is running inside a partner embed iframe.
 * Detected via the `embed=1` query param set by embed.js, with an
 * iframe-context fallback (`window.self !== window.top`).
 */
export function isEmbedded(): boolean {
  if (typeof window === 'undefined') return false;
  const params = new URLSearchParams(window.location.search);
  if (params.get('embed') === '1') return true;
  try {
    return window.self !== window.top;
  } catch {
    // Cross-origin access throws → we are definitely framed.
    return true;
  }
}

export function getEmbedConfig(): EmbedConfig {
  if (typeof window === 'undefined') return DEFAULT_CONFIG;

  const params = new URLSearchParams(window.location.search);
  const partner = params.get('partner') || params.get('embed') || 'default';
  const theme = (params.get('theme') as 'light' | 'dark') || 'light';
  const locale = params.get('locale') || navigator.language || 'en-US';

  // Parse feature flags from URL
  // Preserve boolean, number, and string values rather than coercing everything to boolean
  const featureFlags: Record<string, boolean | number | string> = {};
  params.forEach((value, key) => {
    if (key.startsWith('ff_')) {
      const flagKey = key.slice(3);
      if (value === 'true' || value === '1') {
        featureFlags[flagKey] = true;
      } else if (value === 'false' || value === '0') {
        featureFlags[flagKey] = false;
      } else if (/^\d+$/.test(value)) {
        featureFlags[flagKey] = parseInt(value, 10);
      } else {
        featureFlags[flagKey] = value;
      }
    }
  });

  return { partner, theme, locale, featureFlags, isEmbed: isEmbedded() };
}

const PRODUCTION_API_BASE_URL = 'https://cabinetvision-api.vercel.app';

/**
 * Resolve the API base URL.
 *
 * Prefer the build-time `VITE_API_BASE_URL`. If it is missing (e.g. a
 * production build that forgot to set the env var) AND we are served from a
 * deployed host (not localhost), fall back to the known production API instead
 * of issuing same-origin relative calls that 404/405 against the static host.
 * On localhost we keep relative calls so a local Vite proxy can be used.
 */
function resolveApiBaseUrl(): string {
  const fromEnv = import.meta.env.VITE_API_BASE_URL;
  if (fromEnv) return fromEnv;

  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    const isLocal = host === 'localhost' || host === '127.0.0.1' || host === '0.0.0.0';
    if (!isLocal) return PRODUCTION_API_BASE_URL;
  }

  return '';
}

export const API_BASE_URL = resolveApiBaseUrl();

